import { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  createProductThunk,
  updateProductByIdThunk,
  checkAvailabilityThunk,
  updateLocationStockThunk,
} from "../../../store/thunks/adminProductThunks";
import { getAllCategoriesThunk } from "../../../store/thunks/categoryThunks";
import { getBranchesThunk } from "store/thunks/branchThunks";

// ============= COMPLETE CONSTANTS FROM BACKEND =============
export const PRODUCT_TYPE_ENUM = [
  "GOLD",
  "SILVER",
  "PLATINUM",
  "DIAMOND",
  "STUDDED",
];
export const PRODUCT_STATUS_ENUM = ["ACTIVE", "INACTIVE", "DRAFT", "ARCHIVED"];
export const PRODUCT_GENDER_ENUM = ["MALE", "FEMALE", "UNISEX", "KIDS"];
export const VIEW_ACCESS_ENUM = [
  "B2C_APP",
  "B2C_WEBSITE",
  "B2B_WEBSITE",
  "B2B_APP",
];
export const PURITY_ENUM = ["24K", "22K", "18K", "14K", "925", "950", "PT950"];
export const METAL_COLOR_ENUM = [
  "YELLOW",
  "WHITE",
  "ROSE",
  "TWO_TONE",
  "THREE_TONE",
];
export const PRODUCT_SUBTYPE_ENUM = [
  "RING",
  "NECKLACE",
  "EARRING",
  "BRACELET",
  "PENDANT",
  "CHAIN",
  "BANGLE",
  "ANKLET",
  "NOSE_PIN",
  "MANGALSUTRA",
  "KADA",
  "COIN",
  "BAR",
  "IDOL",
  "OTHER",
];
export const COLLECTION_TYPE_ENUM = [
  "WEDDING",
  "FESTIVAL",
  "DAILY_WEAR",
  "OFFICE_WEAR",
  "PARTY",
  "TRADITIONAL",
  "MODERN",
];
export const OCCASION_ENUM = [
  "WEDDING",
  "ENGAGEMENT",
  "ANNIVERSARY",
  "BIRTHDAY",
  "FESTIVAL",
  "GIFT",
  "INVESTMENT",
];
export const CRAFT_TYPE_ENUM = [
  "HANDMADE",
  "MACHINE_MADE",
  "CAST",
  "HANDCRAFTED",
  "STAMPED",
];
export const TECHNIQUE_ENUM = [
  "KUNDAN",
  "MEENAKARI",
  "TEMPLE",
  "FILIGREE",
  "ANTIQUE",
  "MODERN",
  "TRADITIONAL",
];

// NEW: Stone constants
export const STONE_TYPE_ENUM = [
  "DIAMOND",
  "RUBY",
  "EMERALD",
  "SAPPHIRE",
  "PEARL",
  "TOPAZ",
  "AMETHYST",
  "OTHER",
];
export const STONE_SHAPE_ENUM = [
  "ROUND",
  "PRINCESS",
  "OVAL",
  "MARQUISE",
  "PEAR",
  "CUSHION",
  "EMERALD",
  "ASSCHER",
  "RADIANT",
  "HEART",
];
export const STONE_SETTING_ENUM = [
  "PRONG",
  "BEZEL",
  "CHANNEL",
  "PAVE",
  "TENSION",
  "HALO",
  "CLUSTER",
];

// NEW: Certification constants
export const CERTIFICATION_TYPE_ENUM = [
  "BIS_HALLMARK",
  "GIA",
  "IGI",
  "SGL",
  "HRD",
  "AGS",
  "DGLA",
];
export const CERTIFICATION_AGENCY_ENUM = [
  "BIS",
  "GIA",
  "IGI",
  "SGL",
  "HRD",
  "AGS",
  "DGLA",
];

// ============= UPDATED DEFAULT FORM VALUES =============
export const DEFAULT_PRODUCT_FORM_VALUES = {
  // Basic fields (including new identifiers)
  productId: "", // Auto-generated, read-only in edit mode
  sku: "", // Can be auto-generated or manual
  name: "",
  description: "",
  type: PRODUCT_TYPE_ENUM[0],
  subType: "",
  categories: [],
  collections: [],

  // Weights
  grossWeight: 0,
  netWeight: 0,

  // Metal details
  purity: "",
  metalColor: METAL_COLOR_ENUM[0],
  metalDetails: {
    purity: "",
    color: METAL_COLOR_ENUM[0],
    fineness: null,
    karat: null,
  },

  // NEW: Stone/Diamond Information
  hasStones: false,
  stones: [],
  totalStoneWeight: 0,
  stoneCharges: 0,

  // Manufacturing (updated with batchNumber)
  karigarCode: "",
  craftType: "",
  technique: "",
  manufacturing: {
    karigarCode: "",
    craftType: "",
    technique: "",
    origin: "",
    productionDate: new Date(),
    batchNumber: "", // NEW
  },

  // Stock management
  stock: {
    totalQuantity: 0,
    reservedQuantity: 0,
    availableQuantity: 0,
    minStockLevel: 1,
    maxStockLevel: null,
    locationStock: [],
  },

  // Branch management
  branchInfo: {
    primaryBranch: "",
    availableBranches: [],
    availableStores: [],
  },

  // Status & Display
  status: PRODUCT_STATUS_ENUM[2], // DRAFT
  viewAccess: ["B2C_WEBSITE", "B2C_APP"],
  gender: "UNISEX",
  occasion: [],
  tags: [],
  isFeatured: false,
  isNewArrival: false,
  isBestseller: false,
  isExclusive: false,
  isPrivate: false, // NEW

  // Hallmark
  hallmark: {
    isHallmarked: false,
    huid: "",
    centerCode: "",
    year: new Date().getFullYear().toString(),
    jewellerCode: "",
    purity: "",
  },

  // NEW: Certifications
  certifications: [],

  // NEW: Size/Variant Information
  sizeInfo: {
    hasSize: false,
    availableSizes: [],
    defaultSize: "",
    sizeChart: "",
  },
  isVariant: false,
  parentProduct: null,
  variants: [],
  variantAttribute: "",

  // NEW: Customization Options
  customizable: {
    isCustomizable: false,
    options: [],
  },

  // Pricing config
  pricingConfig: {
    useCustomPricing: false,
    customMakingCharges: null,
    customWastagePercent: null,
    excludeFromOffers: false,
    branchSpecificRules: [],
  },

  // Media
  media: [],

  // NEW: SEO reference
  productSeo: null,

  // NEW: Legacy fields for backward compatibility
  narration: "",
  isStone: false,
  stoneWeight: 0,
};

// ============= STONE HELPER OBJECT =============
export const DEFAULT_STONE = {
  type: STONE_TYPE_ENUM[0],
  weight: 0,
  count: 1,
  color: "",
  clarity: "",
  cut: "",
  shape: STONE_SHAPE_ENUM[0],
  settingType: STONE_SETTING_ENUM[0],
  certification: {
    agency: "",
    certificateNumber: "",
    certificateUrl: "",
  },
  ratePerCarat: 0,
  totalValue: 0,
};

// ============= CERTIFICATION HELPER OBJECT =============
export const DEFAULT_CERTIFICATION = {
  type: CERTIFICATION_TYPE_ENUM[0],
  number: "",
  issuedBy: "",
  issuedDate: new Date(),
  validUntil: null,
  documentUrl: "",
};

// ============= CUSTOMIZATION OPTION HELPER =============
export const DEFAULT_CUSTOMIZATION_OPTION = {
  type: "",
  values: [],
  priceImpact: 0,
};

// ============= UPDATED PARSE FUNCTION =============
function parseExistingProductData(product, primaryBranch) {
  if (!product) return DEFAULT_PRODUCT_FORM_VALUES;

  // Extract category IDs properly
  const categoryIds =
    product.categories
      ?.map((cat) => {
        if (typeof cat === "string") return cat;
        if (typeof cat === "object" && cat !== null) {
          return cat._id || cat.id;
        }
        return null;
      })
      .filter(Boolean) || [];

  return {
    // Basic fields (including identifiers)
    productId: product.productId || "",
    sku: product.sku || "",
    name: product.name || "",
    description: product.description || "",
    type: product.type || DEFAULT_PRODUCT_FORM_VALUES.type,
    subType: product.subType || "",
    categories: categoryIds,
    collections: product.collections || [],

    // Weights
    grossWeight: product.grossWeight || 0,
    netWeight: product.netWeight || 0,

    // Metal details
    purity: product.purity || product.metalDetails?.purity || "",
    metalColor:
      product.metalColor || product.metalDetails?.color || METAL_COLOR_ENUM[0],
    metalDetails: {
      purity: product.metalDetails?.purity || product.purity || "",
      color:
        product.metalDetails?.color ||
        product.metalColor ||
        METAL_COLOR_ENUM[0],
      fineness: product.metalDetails?.fineness || null,
      karat: product.metalDetails?.karat || null,
    },

    // Stone Information
    hasStones: Boolean(product.hasStones),
    stones: product.stones || [],
    totalStoneWeight: product.totalStoneWeight || 0,
    stoneCharges: product.stoneCharges || 0,

    // Manufacturing (with batchNumber)
    karigarCode:
      product.karigarCode || product.manufacturing?.karigarCode || "",
    craftType: product.craftType || product.manufacturing?.craftType || "",
    technique: product.technique || product.manufacturing?.technique || "",
    manufacturing: {
      karigarCode:
        product.manufacturing?.karigarCode || product.karigarCode || "",
      craftType: product.manufacturing?.craftType || product.craftType || "",
      technique: product.manufacturing?.technique || product.technique || "",
      origin: product.manufacturing?.origin || "",
      productionDate: product.manufacturing?.productionDate || new Date(),
      batchNumber: product.manufacturing?.batchNumber || "",
    },

    // Stock management
    stock: {
      totalQuantity: product.stock?.totalQuantity || 0,
      reservedQuantity: product.stock?.reservedQuantity || 0,
      availableQuantity: product.stock?.availableQuantity || 0,
      minStockLevel: product.stock?.minStockLevel || 1,
      maxStockLevel: product.stock?.maxStockLevel || null,
      locationStock: product.stock?.locationStock || [],
    },

    // Branch management
    branchInfo: {
      primaryBranch: product.branchInfo?.primaryBranch || primaryBranch || "",
      availableBranches: product.branchInfo?.availableBranches || [],
      availableStores: product.branchInfo?.availableStores || [],
    },

    // Status & Display
    status: product.status || DEFAULT_PRODUCT_FORM_VALUES.status,
    viewAccess: product.viewAccess || DEFAULT_PRODUCT_FORM_VALUES.viewAccess,
    gender: product.gender || DEFAULT_PRODUCT_FORM_VALUES.gender,
    occasion: product.occasion || [],
    tags: product.tags || [],
    isFeatured: Boolean(product.isFeatured),
    isNewArrival: Boolean(product.isNewArrival),
    isBestseller: Boolean(product.isBestseller),
    isExclusive: Boolean(product.isExclusive),
    isPrivate: Boolean(product.isPrivate),

    // Hallmark
    hallmark: {
      isHallmarked: Boolean(
        product.hallmark?.isHallmarked || product.isHallmarked
      ),
      huid: product.hallmark?.huid || product.huid || "",
      centerCode:
        product.hallmark?.centerCode || product.hallmarkCenterCode || "",
      year:
        product.hallmark?.year ||
        product.hallmarkYear ||
        new Date().getFullYear().toString(),
      jewellerCode: product.hallmark?.jewellerCode || "",
      purity: product.hallmark?.purity || product.purity || "",
    },

    // Certifications
    certifications: product.certifications || [],

    // Size/Variant Information
    sizeInfo: {
      hasSize: Boolean(product.sizeInfo?.hasSize),
      availableSizes: product.sizeInfo?.availableSizes || [],
      defaultSize: product.sizeInfo?.defaultSize || "",
      sizeChart: product.sizeInfo?.sizeChart || "",
    },
    isVariant: Boolean(product.isVariant),
    parentProduct: product.parentProduct || null,
    variants: product.variants || [],
    variantAttribute: product.variantAttribute || "",

    // Customization Options
    customizable: {
      isCustomizable: Boolean(product.customizable?.isCustomizable),
      options: product.customizable?.options || [],
    },

    // Pricing config
    pricingConfig: {
      useCustomPricing: Boolean(product.pricingConfig?.useCustomPricing),
      customMakingCharges: product.pricingConfig?.customMakingCharges || null,
      customWastagePercent: product.pricingConfig?.customWastagePercent || null,
      excludeFromOffers: Boolean(product.pricingConfig?.excludeFromOffers),
      branchSpecificRules: product.pricingConfig?.branchSpecificRules || [],
    },

    // Media
    media: product.media || [],

    // SEO reference
    productSeo: product.productSeo || null,

    // Legacy fields
    narration: product.narration || "",
    isStone: Boolean(product.isStone),
    stoneWeight: product.stoneWeight || 0,
    _id: product._id,
  };
}

// ============= MAIN ENHANCED FORM HOOK =============
export const useEnhancedProductForm = (initialData = null, mode = "add") => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { user } = useSelector((state) => state.auth);
  const { categories } = useSelector((state) => state.category);

  // Get branches from user
  const userBranchAccess = user?.branchAccess || {};
  const { branches: availableBranchesData } = useSelector(
    (state) => state.branch
  );

  // Extract branch codes from complex branch objects
  const availableBranches = useMemo(() => {
    if (!availableBranchesData) return [];
    return availableBranchesData
      .map((branch) => {
        if (branch && typeof branch === "object") {
          return branch.code || "";
        }
        return branch;
      })
      .filter(Boolean);
  }, [availableBranchesData]);

  // Permissions
  const productPermissions = user?.accessControl?.products?.permissions || {};
  const permissions = {
    canCreate: true ?? productPermissions.create,
    canUpdate: true ?? productPermissions.update,
    canManageStock: true ?? productPermissions.manageStock,
    canManagePricing: true ?? productPermissions.managePricing,
  };

  // Form state - Initialize with parsed data
  const [formData, setFormData] = useState(() => {
    if (mode === "edit" && initialData) {
      return parseExistingProductData(
        initialData,
        userBranchAccess.primaryBranch
      );
    }

    const baseData = { ...DEFAULT_PRODUCT_FORM_VALUES };
    // Set primary branch for new products
    if (userBranchAccess.primaryBranch) {
      baseData.branchInfo.primaryBranch = userBranchAccess.primaryBranch;
      baseData.branchInfo.availableBranches = [userBranchAccess.primaryBranch];
    }
    return baseData;
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [locationStocks, setLocationStocks] = useState([]);

  // Load categories
  useEffect(() => {
    dispatch(getAllCategoriesThunk());
  }, [dispatch]);

  // Load branches
  useEffect(() => {
    dispatch(getBranchesThunk({ isActive: true }));
  }, [dispatch]);

  // Update form data when initial data changes (for edit mode)
  useEffect(() => {
    if (mode === "edit" && initialData) {
      const parsedData = parseExistingProductData(
        initialData,
        userBranchAccess.primaryBranch
      );
      setFormData(parsedData);

      // Create mutable copies of location stocks
      if (
        initialData.stock?.locationStock &&
        Array.isArray(initialData.stock.locationStock)
      ) {
        const mutableLocationStocks = initialData.stock.locationStock.map(
          (stock) => ({
            branchCode: stock.branchCode || "",
            storeCode: stock.storeCode || null,
            quantity: stock.quantity || 0,
            reserved: stock.reserved || 0,
            available: stock.available || 0,
            displayQuantity: stock.displayQuantity || 0,
            lastUpdated: stock.lastUpdated || new Date(),
            _id: stock._id,
            id: stock.id,
          })
        );
        setLocationStocks(mutableLocationStocks);
      }
    }
  }, [initialData, mode, userBranchAccess.primaryBranch]);

  // ============= HANDLERS =============
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle nested fields
    if (name.includes(".")) {
      const keys = name.split(".");
      if (keys.length === 2) {
        const [parent, child] = keys;
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === "checkbox" ? checked : value,
          },
        }));
      } else if (keys.length === 3) {
        // Handle deeper nesting (e.g., customizable.options.0)
        const [parent, child, index] = keys;
        setFormData((prev) => ({
          ...prev,
          [parent]: {
            ...prev[parent],
            [child]: type === "checkbox" ? checked : value,
          },
        }));
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  // ============= STONE MANAGEMENT =============
  const addStone = () => {
    setFormData((prev) => ({
      ...prev,
      stones: [...prev.stones, { ...DEFAULT_STONE }],
      hasStones: true,
    }));
  };

  const updateStone = (index, field, value) => {
    setFormData((prev) => {
      const updatedStones = [...prev.stones];

      // Handle nested certification fields
      if (field.startsWith("certification.")) {
        const certField = field.split(".")[1];
        updatedStones[index] = {
          ...updatedStones[index],
          certification: {
            ...updatedStones[index].certification,
            [certField]: value,
          },
        };
      } else {
        updatedStones[index] = {
          ...updatedStones[index],
          [field]: value,
        };
      }

      // Calculate total value if rate and weight are present
      if (field === "ratePerCarat" || field === "weight") {
        const weight = field === "weight" ? value : updatedStones[index].weight;
        const rate =
          field === "ratePerCarat" ? value : updatedStones[index].ratePerCarat;
        updatedStones[index].totalValue = weight * rate;
      }

      // Recalculate total stone weight and charges
      const totalStoneWeight = updatedStones.reduce(
        (sum, stone) => sum + (stone.weight || 0),
        0
      );
      const stoneCharges = updatedStones.reduce(
        (sum, stone) => sum + (stone.totalValue || 0),
        0
      );

      return {
        ...prev,
        stones: updatedStones,
        totalStoneWeight,
        stoneCharges,
        isStone: updatedStones.length > 0, // Legacy field
        stoneWeight: totalStoneWeight, // Legacy field
      };
    });
  };

  const removeStone = (index) => {
    setFormData((prev) => {
      const updatedStones = prev.stones.filter((_, i) => i !== index);
      const totalStoneWeight = updatedStones.reduce(
        (sum, stone) => sum + (stone.weight || 0),
        0
      );
      const stoneCharges = updatedStones.reduce(
        (sum, stone) => sum + (stone.totalValue || 0),
        0
      );

      return {
        ...prev,
        stones: updatedStones,
        hasStones: updatedStones.length > 0,
        totalStoneWeight,
        stoneCharges,
        isStone: updatedStones.length > 0,
        stoneWeight: totalStoneWeight,
      };
    });
  };

  // ============= CERTIFICATION MANAGEMENT =============
  const addCertification = () => {
    setFormData((prev) => ({
      ...prev,
      certifications: [...prev.certifications, { ...DEFAULT_CERTIFICATION }],
    }));
  };

  const updateCertification = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.certifications];
      updated[index] = {
        ...updated[index],
        [field]: value,
      };
      return { ...prev, certifications: updated };
    });
  };

  const removeCertification = (index) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  };

  // ============= SIZE MANAGEMENT =============
  const addSize = (size) => {
    if (size && !formData.sizeInfo.availableSizes.includes(size)) {
      setFormData((prev) => ({
        ...prev,
        sizeInfo: {
          ...prev.sizeInfo,
          hasSize: true,
          availableSizes: [...prev.sizeInfo.availableSizes, size],
          defaultSize: prev.sizeInfo.defaultSize || size, // Set first as default
        },
      }));
    }
  };

  const removeSize = (size) => {
    setFormData((prev) => {
      const updated = prev.sizeInfo.availableSizes.filter((s) => s !== size);
      return {
        ...prev,
        sizeInfo: {
          ...prev.sizeInfo,
          availableSizes: updated,
          hasSize: updated.length > 0,
          defaultSize:
            prev.sizeInfo.defaultSize === size
              ? updated[0] || ""
              : prev.sizeInfo.defaultSize,
        },
      };
    });
  };

  // ============= CUSTOMIZATION OPTIONS =============
  const addCustomizationOption = () => {
    setFormData((prev) => ({
      ...prev,
      customizable: {
        ...prev.customizable,
        isCustomizable: true,
        options: [
          ...prev.customizable.options,
          { ...DEFAULT_CUSTOMIZATION_OPTION },
        ],
      },
    }));
  };

  const updateCustomizationOption = (index, field, value) => {
    setFormData((prev) => {
      const updated = [...prev.customizable.options];
      if (field === "values" && typeof value === "string") {
        // Convert comma-separated string to array
        updated[index] = {
          ...updated[index],
          values: value
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean),
        };
      } else {
        updated[index] = {
          ...updated[index],
          [field]: value,
        };
      }
      return {
        ...prev,
        customizable: {
          ...prev.customizable,
          options: updated,
        },
      };
    });
  };

  const removeCustomizationOption = (index) => {
    setFormData((prev) => {
      const updated = prev.customizable.options.filter((_, i) => i !== index);
      return {
        ...prev,
        customizable: {
          ...prev.customizable,
          options: updated,
          isCustomizable: updated.length > 0,
        },
      };
    });
  };

  // ============= LOCATION STOCK MANAGEMENT (existing) =============
  const addLocationStock = (branchCode, storeCode = null) => {
    const exists = locationStocks.some(
      (loc) => loc.branchCode === branchCode && loc.storeCode === storeCode
    );

    if (!exists) {
      setLocationStocks((prev) => [
        ...prev,
        {
          branchCode,
          storeCode,
          quantity: 0,
          reserved: 0,
          available: 0,
          displayQuantity: 0,
          lastUpdated: new Date(),
        },
      ]);
    }
  };

  const updateLocationStock = (index, field, value) => {
    setLocationStocks((prev) => {
      const updated = prev.map((item, i) => {
        if (i === index) {
          const newItem = { ...item };
          newItem[field] = value;

          if (field === "quantity" || field === "reserved") {
            const qty = newItem.quantity === "" ? 0 : newItem.quantity;
            const res = newItem.reserved === "" ? 0 : newItem.reserved;
            newItem.available = qty - res;
          }

          return newItem;
        }
        return item;
      });
      return updated;
    });
  };

  const removeLocationStock = (index) => {
    setLocationStocks((prev) => prev.filter((_, i) => i !== index));
  };

  const calculateTotalStock = () => {
    const total = locationStocks.reduce(
      (acc, loc) => ({
        quantity: acc.quantity + (loc.quantity || 0),
        reserved: acc.reserved + (loc.reserved || 0),
        available: acc.available + (loc.available || 0),
      }),
      { quantity: 0, reserved: 0, available: 0 }
    );
    return total;
  };

  // ============= VALIDATION =============
  const validateForm = () => {
    const newErrors = {};

    // For Quick Mode, we only validate essential fields
    const isQuickMode =
      !formData.hasStones &&
      !formData.hallmark.isHallmarked &&
      !formData.sizeInfo.hasSize &&
      !formData.customizable.isCustomizable;

    // Basic validations - ALWAYS REQUIRED
    if (!formData.name || formData.name.length < 3) {
      newErrors.name = "Product name is required (min 3 characters)";
    }
    if (!formData.type) newErrors.type = "Product type is required";
    if (!formData.subType) newErrors.subType = "Product subtype is required";

    // Weight validations
    if (formData.grossWeight <= 0) {
      newErrors.grossWeight = "Gross weight must be greater than 0";
    }
    if (formData.netWeight <= 0) {
      newErrors.netWeight = "Net weight must be greater than 0";
    }
    if (formData.netWeight > formData.grossWeight) {
      newErrors.netWeight = "Net weight cannot exceed gross weight";
    }

    // Metal details
    if (!formData.purity) newErrors.purity = "Purity is required";
    if (!formData.karigarCode) {
      newErrors.karigarCode = "Karigar code is required";
    }

    // Branch validation - Check if it's auto-filled
    if (!formData.branchInfo.primaryBranch && !userBranchAccess.primaryBranch) {
      newErrors["branchInfo.primaryBranch"] = "Primary branch is required";
    }

    // Stock validation - MODIFIED FOR QUICK MODE
    // For Quick Mode, we'll automatically create a default location stock if needed
    if (mode === "add" && !isQuickMode) {
      if (locationStocks.length === 0 && permissions.canManageStock) {
        newErrors.stock = "At least one location stock is required";
      }
    }

    // CONDITIONAL VALIDATIONS - Only if features are enabled

    // Hallmark validation - only if enabled
    if (formData.hallmark.isHallmarked) {
      if (!formData.hallmark.huid) {
        newErrors["hallmark.huid"] = "HUID is required for hallmarked products";
      }
      if (!formData.hallmark.centerCode) {
        newErrors["hallmark.centerCode"] = "Center code is required";
      }
    }

    // Stone validation - only if enabled
    if (formData.hasStones && formData.stones.length === 0) {
      newErrors.stones =
        "At least one stone entry is required when product has stones";
    }

    // Size validation - only if enabled
    if (
      formData.sizeInfo.hasSize &&
      formData.sizeInfo.availableSizes.length === 0
    ) {
      newErrors["sizeInfo.availableSizes"] = "At least one size is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ============= SUBMIT HANDLER =============
  const handleSubmit = async (mediaFiles, displayMedia) => {
    // Check permissions
    if (mode === "add" && !permissions.canCreate) {
      toast.error("You do not have permission to create products");
      return;
    }
    if (mode === "edit" && !permissions.canUpdate) {
      toast.error("You do not have permission to update products");
      return;
    }

    // Determine if we're in Quick Mode
    const isQuickMode =
      mode === "add" &&
      !formData.hasStones &&
      !formData.hallmark.isHallmarked &&
      !formData.sizeInfo.hasSize &&
      !formData.customizable.isCustomizable;

    // AUTO-CREATE DEFAULT STOCK FOR QUICK MODE
    let tempLocationStocks = [...locationStocks];
    if (
      mode === "add" &&
      tempLocationStocks.length === 0 &&
      formData.stock.totalQuantity > 0
    ) {
      // Create a default location stock entry with primary branch
      const primaryBranch =
        formData.branchInfo.primaryBranch ||
        userBranchAccess.primaryBranch ||
        availableBranches[0]; // Use first available branch as fallback

      if (primaryBranch) {
        tempLocationStocks = [
          {
            branchCode: primaryBranch,
            storeCode: null,
            quantity: formData.stock.totalQuantity || 0,
            reserved: 0,
            available: formData.stock.totalQuantity || 0,
            displayQuantity: 0,
            lastUpdated: new Date(),
          },
        ];
      }
    }

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSubmitting(true);
    try {
      const submitData = new FormData();

      // Basic fields (including new identifiers)
      if (mode === "edit" && formData.productId) {
        submitData.append("productId", formData.productId);
      }
      if (formData.sku) {
        submitData.append("sku", formData.sku);
      }
      submitData.append("name", formData.name);
      submitData.append("description", formData.description || "");
      submitData.append("type", formData.type);
      submitData.append("subType", formData.subType);
      submitData.append("status", formData.status);
      submitData.append("gender", formData.gender);
      submitData.append("narration", formData.narration || "");

      // Weights
      submitData.append("grossWeight", formData.grossWeight);
      submitData.append("netWeight", formData.netWeight);

      // Metal details
      submitData.append(
        "purity",
        formData.purity || formData.metalDetails?.purity || ""
      );
      submitData.append(
        "metalColor",
        formData.metalColor || formData.metalDetails?.color || "YELLOW"
      );

      // Manufacturing (with batchNumber)
      submitData.append(
        "karigarCode",
        formData.karigarCode || formData.manufacturing?.karigarCode || ""
      );
      if (formData.craftType || formData.manufacturing?.craftType) {
        submitData.append(
          "craftType",
          formData.craftType || formData.manufacturing.craftType
        );
      }
      if (formData.technique || formData.manufacturing?.technique) {
        submitData.append(
          "technique",
          formData.technique || formData.manufacturing.technique
        );
      }
      if (formData.manufacturing?.origin) {
        submitData.append("origin", formData.manufacturing.origin);
      }
      if (formData.manufacturing?.batchNumber) {
        submitData.append("batchNumber", formData.manufacturing.batchNumber);
      }

      // Categories
      if (formData.categories && formData.categories.length > 0) {
        formData.categories.forEach((catId) => {
          if (catId) {
            submitData.append("categories[]", catId);
          }
        });
      }

      // Other arrays
      (formData.collections || []).forEach((item) => {
        submitData.append("collections[]", item);
      });
      (formData.occasion || []).forEach((item) => {
        submitData.append("occasion[]", item);
      });
      (formData.tags || []).forEach((item) => {
        submitData.append("tags[]", item);
      });
      (formData.viewAccess || ["B2C_WEBSITE", "B2C_APP"]).forEach((item) => {
        submitData.append("viewAccess[]", item);
      });

      // Stones (NEW) - only if enabled
      if (formData.hasStones && formData.stones.length > 0) {
        submitData.append("hasStones", "true");
        submitData.append("stones", JSON.stringify(formData.stones));
        submitData.append("totalStoneWeight", formData.totalStoneWeight);
        submitData.append("stoneCharges", formData.stoneCharges || 0);
        // Legacy fields
        submitData.append("isStone", "true");
        submitData.append("stoneWeight", formData.totalStoneWeight);
      } else {
        submitData.append("hasStones", "false");
        submitData.append("isStone", "false");
      }

      // Certifications (NEW) - only if present
      if (formData.certifications && formData.certifications.length > 0) {
        submitData.append(
          "certifications",
          JSON.stringify(formData.certifications)
        );
      }

      // Size/Variant Information (NEW) - only if enabled
      if (formData.sizeInfo.hasSize) {
        submitData.append("sizeInfo", JSON.stringify(formData.sizeInfo));
      }
      if (formData.isVariant) {
        submitData.append("isVariant", "true");
        if (formData.parentProduct) {
          submitData.append("parentProduct", formData.parentProduct);
        }
        if (formData.variantAttribute) {
          submitData.append("variantAttribute", formData.variantAttribute);
        }
      }
      if (formData.variants && formData.variants.length > 0) {
        submitData.append("variants", JSON.stringify(formData.variants));
      }

      // Customization Options (NEW) - only if enabled
      if (formData.customizable.isCustomizable) {
        submitData.append(
          "customizable",
          JSON.stringify(formData.customizable)
        );
      }

      // FIXED: Pricing Configuration - Send minimal config for Quick Mode
      let pricingConfigObject;

      if (isQuickMode) {
        // Quick Mode - absolutely no pricing rules
        pricingConfigObject = {
          useCustomPricing: false,
          customMakingCharges: null,
          customWastagePercent: null,
          excludeFromOffers: false,
          branchSpecificRules: [], // Always empty for Quick Mode
        };
      } else {
        // Advanced/Edit Mode - check if rules actually exist
        pricingConfigObject = {
          useCustomPricing: formData.pricingConfig?.useCustomPricing || false,
          customMakingCharges:
            formData.pricingConfig?.customMakingCharges || null,
          customWastagePercent:
            formData.pricingConfig?.customWastagePercent || null,
          excludeFromOffers: formData.pricingConfig?.excludeFromOffers || false,
          branchSpecificRules: [], // Start empty
        };

        // Only add rules if they're valid and we're in advanced mode
        if (formData.pricingConfig?.branchSpecificRules?.length > 0) {
          // Check if the rules are actually valid (have been selected from the UI)
          // Don't include default or invalid rules
          const validRules = formData.pricingConfig.branchSpecificRules
            .filter((rule) => {
              // Skip if this is a default/invalid rule ID
              if (rule.pricingRuleId === "691e03e94cc708224d86cb2f") {
                console.warn("Skipping invalid default pricing rule");
                return false;
              }
              // Only include if both fields are present and valid
              return rule.pricingRuleId && rule.branchCode;
            })
            .map((rule) => ({
              branchCode: rule.branchCode,
              pricingRuleId: rule.pricingRuleId,
              notes: rule.notes || "",
            }));

          pricingConfigObject.branchSpecificRules = validRules;
        }
      }

      // Log for debugging
      console.log("Final pricing config:", pricingConfigObject);
      submitData.append("pricingConfig", JSON.stringify(pricingConfigObject));

      // Branch info
      const primaryBranch =
        formData.branchInfo?.primaryBranch ||
        formData.primaryBranch ||
        userBranchAccess.primaryBranch;
      if (primaryBranch) {
        submitData.append("primaryBranch", primaryBranch);
        submitData.append("branchCode", primaryBranch);
      }

      // Stock - use tempLocationStocks
      const totalStock =
        tempLocationStocks.length > 0
          ? tempLocationStocks.reduce(
              (acc, loc) => ({
                quantity: acc.quantity + (loc.quantity || 0),
                reserved: acc.reserved + (loc.reserved || 0),
                available: acc.available + (loc.available || 0),
              }),
              { quantity: 0, reserved: 0, available: 0 }
            )
          : {
              quantity: formData.stock.totalQuantity || 0,
              reserved: 0,
              available: formData.stock.totalQuantity || 0,
            };

      if (tempLocationStocks.length > 0) {
        const stockObject = {
          totalQuantity: totalStock.quantity || 0,
          reservedQuantity: totalStock.reserved || 0,
          availableQuantity: totalStock.available || 0,
          minStockLevel: formData.stock?.minStockLevel || 1,
          maxStockLevel: formData.stock?.maxStockLevel || null,
          locationStock: tempLocationStocks.map((stock) => ({
            branchCode: stock.branchCode,
            storeCode: stock.storeCode || null,
            quantity: Number(stock.quantity) || 0,
            reserved: Number(stock.reserved) || 0,
            available: Number(stock.available) || 0,
            displayQuantity: Number(stock.displayQuantity) || 0,
            _id: stock._id || undefined,
          })),
        };
        submitData.append("stockData", JSON.stringify(stockObject));
        submitData.append("stock", stockObject.totalQuantity);
        submitData.append("minStockLevel", stockObject.minStockLevel);
        if (stockObject.maxStockLevel) {
          submitData.append("maxStockLevel", stockObject.maxStockLevel);
        }
      } else {
        // Fallback - should not happen if we auto-create stock
        submitData.append("stock", formData.stock?.totalQuantity || 0);
        submitData.append("minStockLevel", formData.stock?.minStockLevel || 1);
        if (formData.stock?.maxStockLevel) {
          submitData.append("maxStockLevel", formData.stock.maxStockLevel);
        }
      }

      // Hallmark - only if enabled
      if (formData.hallmark?.isHallmarked) {
        submitData.append("isHallmarked", "true");
        submitData.append("huid", formData.hallmark.huid || "");
        submitData.append(
          "hallmarkCenterCode",
          formData.hallmark.centerCode || ""
        );
        submitData.append(
          "hallmarkYear",
          formData.hallmark.year || new Date().getFullYear().toString()
        );
        submitData.append(
          "hallmarkJewellerCode",
          formData.hallmark.jewellerCode || ""
        );
        submitData.append(
          "hallmarkPurity",
          formData.hallmark.purity || formData.purity || ""
        );
      } else {
        submitData.append("isHallmarked", "false");
      }

      // Display flags
      submitData.append("isFeatured", formData.isFeatured || false);
      submitData.append("isNewArrival", formData.isNewArrival || false);
      submitData.append("isBestseller", formData.isBestseller || false);
      submitData.append("isExclusive", formData.isExclusive || false);
      submitData.append("isPrivate", formData.isPrivate || false);

      // Handle media for edit mode
      if (mode === "edit" && displayMedia) {
        const existingMedia = displayMedia.filter(
          (m) =>
            m.isExisting && !mediaFiles.pendingRemovals?.includes(m.id || m._id)
        );

        if (existingMedia.length > 0) {
          submitData.append(
            "existingMedia",
            JSON.stringify(
              existingMedia.map((media, index) => ({
                id: media._id || media.id,
                url: media.url,
                type: media.type,
                isHero: media.isHero || false,
                order: index,
                altText: media.altText || "",
              }))
            )
          );
        }

        (mediaFiles.pendingRemovals || []).forEach((id) => {
          submitData.append("mediaToRemove[]", id);
        });
      }

      // Add new media files
      if (mediaFiles.images) {
        mediaFiles.images.forEach((file) => {
          submitData.append("images", file);
        });
      }
      if (mediaFiles.videos) {
        mediaFiles.videos.forEach((file) => {
          submitData.append("videos", file);
        });
      }

      // Log payload for debugging (in development only)
      if (process.env.NODE_ENV === "development") {
        console.log("Submitting product data (Quick Mode:", isQuickMode, ")");
        console.log("Pricing Config:", pricingConfigObject);
        console.log("Stock Data:", tempLocationStocks);
      }

      // Submit
      if (mode === "add") {
        const result = await dispatch(createProductThunk(submitData)).unwrap();
        toast.success("Product created successfully");
        if (result.data?._id) {
          navigate(`/products/${result.data._id}`);
        } else {
          navigate("/products");
        }
      } else {
        await dispatch(
          updateProductByIdThunk({
            id: initialData._id,
            productData: submitData,
          })
        ).unwrap();
        toast.success("Product updated successfully");
        navigate("/products");
      }
    } catch (error) {
      console.error("Submit error:", error);
      toast.error(error.message || `Failed to ${mode} product`);
    } finally {
      setSubmitting(false);
    }
  };

  return {
    formData,
    setFormData,
    errors,
    setErrors,
    submitting,
    handleChange,
    handleSubmit,
    categories,
    availableBranches,
    availableBranchesData,
    permissions,
    locationStocks,
    addLocationStock,
    updateLocationStock,
    removeLocationStock,
    calculateTotalStock,
    // NEW: Stone management
    addStone,
    updateStone,
    removeStone,
    // NEW: Certification management
    addCertification,
    updateCertification,
    removeCertification,
    // NEW: Size management
    addSize,
    removeSize,
    // NEW: Customization management
    addCustomizationOption,
    updateCustomizationOption,
    removeCustomizationOption,
    // Constants exports for use in components
    STONE_TYPE_ENUM,
    STONE_SHAPE_ENUM,
    STONE_SETTING_ENUM,
    CERTIFICATION_TYPE_ENUM,
    CERTIFICATION_AGENCY_ENUM,
    CRAFT_TYPE_ENUM, // ADD THIS
    TECHNIQUE_ENUM, // ADD THIS
    PRODUCT_GENDER_ENUM, // ADD THIS
  };
};

// Keep the existing useMediaHandler hook as is...
export const useMediaHandler = (initialData = null, mode = "add") => {
  const [mediaFiles, setMediaFiles] = useState({
    images: [],
    videos: [],
    existingMedia: [],
    pendingRemovals: [],
  });

  const [displayMedia, setDisplayMedia] = useState([]);

  useEffect(() => {
    if (mode === "edit" && initialData?.media?.length > 0) {
      initializeExistingMedia(initialData.media);
    }
  }, [initialData, mode]);

  const initializeExistingMedia = (media) => {
    let hasHero = media.some((item) => item.isHero);

    const existingMediaWithIds = media.map((item, index) => {
      const isVideo =
        item.type === "video" ||
        item.url?.includes(".mp4") ||
        item.url?.includes(".webm") ||
        item.url?.includes(".mov");

      const mediaItem = {
        id: item._id || item.id || `existing-${index}`,
        _id: item._id || item.id,
        url: item.url,
        type: isVideo ? "video" : "image",
        isExisting: true,
        isNew: false,
        isHero: item.isHero || false,
        order: item.order || index,
        thumbnail: item.thumbnail || item.url,
      };

      if (!hasHero && !isVideo && index === 0) {
        mediaItem.isHero = true;
        hasHero = true;
      }

      return mediaItem;
    });

    setMediaFiles((prev) => ({ ...prev, existingMedia: existingMediaWithIds }));
    setDisplayMedia(existingMediaWithIds);
  };

  const handleMediaUpload = async (files) => {
    const newImages = [];
    const newVideos = [];

    for (const file of files) {
      if (file.type.startsWith("video/")) {
        newVideos.push(file);
      } else {
        newImages.push(file);
      }
    }

    setMediaFiles((prev) => ({
      ...prev,
      images: [...prev.images, ...newImages],
      videos: [...prev.videos, ...newVideos],
    }));

    return files.map(() => ({ isNew: true }));
  };

  const handleMediaDelete = async (mediaId) => {
    const mediaItem = displayMedia.find(
      (m) => m.id === mediaId || m._id === mediaId
    );
    if (!mediaItem || !mediaItem.isNew) return;

    const updatedImages = [...mediaFiles.images];
    const updatedVideos = [...mediaFiles.videos];
    const isVideo = mediaItem.type === "video";

    const mediasBefore = displayMedia.filter(
      (m) =>
        m.isNew &&
        m.type === mediaItem.type &&
        displayMedia.indexOf(m) < displayMedia.indexOf(mediaItem)
    ).length;

    if (isVideo) {
      updatedVideos.splice(mediasBefore, 1);
    } else {
      updatedImages.splice(mediasBefore, 1);
    }

    setMediaFiles((prev) => ({
      ...prev,
      images: updatedImages,
      videos: updatedVideos,
    }));

    const updatedDisplayMedia = displayMedia.filter(
      (m) => m.id !== mediaId && m._id !== mediaId
    );

    if (mediaItem.isHero && updatedDisplayMedia.length > 0) {
      const firstImage = updatedDisplayMedia.find((m) => m.type !== "video");
      if (firstImage) {
        updatedDisplayMedia.forEach((m) => {
          m.isHero = (m.id || m._id) === (firstImage.id || firstImage._id);
        });
      }
    }

    setDisplayMedia(updatedDisplayMedia);
  };

  const handlePendingRemovalToggle = (mediaId) => {
    setMediaFiles((prev) => {
      const isPending = prev.pendingRemovals.includes(mediaId);

      if (isPending) {
        return {
          ...prev,
          pendingRemovals: prev.pendingRemovals.filter((id) => id !== mediaId),
        };
      } else {
        const mediaItem = displayMedia.find((m) => (m.id || m._id) === mediaId);
        if (mediaItem?.isHero) {
          const newHero = displayMedia.find(
            (m) =>
              (m.id || m._id) !== mediaId &&
              !prev.pendingRemovals.includes(m.id || m._id) &&
              m.type !== "video"
          );
          if (newHero) {
            displayMedia.forEach((m) => {
              m.isHero = (m.id || m._id) === (newHero.id || newHero._id);
            });
            setDisplayMedia([...displayMedia]);
          }
        }
        return { ...prev, pendingRemovals: [...prev.pendingRemovals, mediaId] };
      }
    });
  };

  const handleMediaChange = (newMedia) => setDisplayMedia(newMedia);

  return {
    mediaFiles,
    displayMedia,
    handleMediaUpload,
    handleMediaDelete,
    handlePendingRemovalToggle,
    handleMediaChange,
  };
};
